import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2, KeyRound, Shield, Mail } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { loginWithPassword } from '../lib/authFlow';
import PublicWhatsAppSupportButton from './PublicWhatsAppSupportButton.jsx';

export default function AcceptInvitationView({ onOpenPrivacyPolicy, onOpenTermsOfUse, whatsappSupport }) {
  const clearRouteState = () => {
    try { window.sessionStorage && window.sessionStorage.removeItem('sofera:route_state:v1'); } catch {}
  };
  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Digite sua senha', color: 'bg-slate-700' });
  const [inviteCode, setInviteCode] = useState('');
  const passwordChecks = [
    { key: 'length', label: 'Pelo menos 8 caracteres', met: form.password.length >= 8 },
    { key: 'uppercase', label: 'Pelo menos 1 letra maiúscula', met: /[A-Z]/.test(form.password) },
    { key: 'lowercase', label: 'Pelo menos 1 letra minúscula', met: /[a-z]/.test(form.password) },
    { key: 'numbers', label: 'Pelo menos 1 número', met: /[0-9]/.test(form.password) },
    { key: 'special', label: 'Pelo menos 1 caractere especial', met: /[!@#$%^&*(),.?":{}|<>]/.test(form.password) },
    { key: 'match', label: 'As senhas coincidem', met: !!form.confirmPassword && form.password === form.confirmPassword }
  ];
  const passwordRulesPassed = passwordChecks.filter((check) => check.key !== 'match').every((check) => check.met);
  const passwordsMatch = passwordChecks.find((check) => check.key === 'match')?.met || false;

  // Neutraliza mensagens cruas em inglês vindas do servidor de Auth do Supabase
  // (ex.: "Password is known to be weak and easy to guess, please choose a different one.").
  const humanizeServerError = (errCode, rawMessage) => {
    const lower = String(rawMessage || '').toLowerCase();
    if (
      errCode === 'weak_password' ||
      lower.includes('weak_password') ||
      lower.includes('known to be weak') ||
      lower.includes('easy to guess') ||
      lower.includes('pwned') ||
      lower.includes('leaked password')
    ) {
      return 'Sua senha não atende aos requisitos de segurança. Revise os itens abaixo.';
    }
    return String(rawMessage || 'Não foi possível definir sua senha. Tente novamente.');
  };

  // Mantemos a sessão caso exista; convites são validados via Edge Function sem exigir logout

  // Validar força da senha
  const validatePasswordStrength = (password) => {
    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    Object.values(checks).forEach(passed => {
      if (passed) score++;
    });

    let label = 'Digite sua senha';
    let color = 'bg-slate-700';

    if (!password) {
      label = 'Digite sua senha';
      color = 'bg-slate-700';
    } else if (score <= 1) {
      label = 'Muito fraca';
      color = 'bg-red-500';
    } else if (score <= 2) {
      label = 'Fraca';
      color = 'bg-orange-500';
    } else if (score <= 3) {
      label = 'Média';
      color = 'bg-yellow-500';
    } else if (score <= 4) {
      label = 'Boa';
      color = 'bg-green-500';
    } else {
      label = 'Excelente';
      color = 'bg-green-600';
    }

    return { score, label, color };
  };

  useEffect(() => {
    if (success) return;
    validateInvitationByCode();
  }, [success]);

  // Atualizar força da senha quando a senha muda
  useEffect(() => {
    const strength = validatePasswordStrength(form.password);
    setPasswordStrength(strength);
  }, [form.password]);

  const getInviteCodeFromUrl = () => {
    try {
      const search = new URLSearchParams(window.location.search || '');
      return String(search.get('invite_code') || search.get('code') || '').trim().toUpperCase();
    } catch {
      return '';
    }
  };

  const validateInvitationByCode = async () => {
    try {
      setLoading(true);
      setError(null);
      const code = getInviteCodeFromUrl();
      setInviteCode(code);
      if (!code) {
        setError({ type: 'invitation_invalid', message: 'Link de convite inválido. Solicite um novo convite.' });
        setLoading(false);
        return;
      }

      const { data, error: fnError } = await supabase.functions.invoke('accept-invitation', {
        body: {
          action: 'validate',
          invite_code: code,
        },
      });

      if (fnError) {
        setError({ type: 'network_error', message: 'Não foi possível validar seu convite agora. Tente novamente.' });
        setLoading(false);
        return;
      }

      if (!data?.valid) {
        const errCode = String(data?.error || '');
        if (errCode === 'invite_expired') {
          setError({ type: 'invitation_expired', message: 'Este convite expirou. Peça um novo convite ao administrador.' });
        } else {
          setError({ type: 'invitation_invalid', message: 'Convite inválido ou indisponível. Solicite um novo convite.' });
        }
        setLoading(false);
        return;
      }

      setInvitation({
        valid: true,
        email: String(data?.email || '').toLowerCase(),
      });
      setLoading(false);
    } catch {
      setLoading(false);
      setError({ type: 'network_error', message: 'Erro de conexão. Tente novamente.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.password || !form.confirmPassword) {
      setError({ type: 'validation', message: 'Por favor, preencha todos os campos de senha.' });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError({ type: 'validation', message: 'As senhas não conferem.' });
      return;
    }

    if (!passwordRulesPassed) {
      setError({ type: 'validation', message: 'Sua senha precisa atender a todos os requisitos de segurança.' });
      return;
    }

    if (!form.acceptTerms) {
      setError({ type: 'validation', message: 'Você deve aceitar os termos de uso e política de privacidade.' });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (!inviteCode) {
        setError({ type: 'invitation_invalid', message: 'Código do convite inválido. Solicite um novo convite.' });
        return;
      }
      const { data, error: fnError } = await supabase.functions.invoke('accept-invitation', {
        body: {
          action: 'accept',
          invite_code: inviteCode,
          password: form.password,
          accept_terms: true,
        },
      });

      if (fnError) {
        setError({ type: 'network_error', message: 'Não foi possível concluir o convite agora. Tente novamente.' });
        return;
      }

      if (!data?.success) {
        const errCode = String(data?.error || '');
        if (errCode === 'invite_expired') {
          setError({ type: 'invitation_expired', message: 'Este convite expirou. Peça um novo convite ao administrador.' });
          return;
        }
        if (errCode === 'invite_not_found' || errCode === 'invite_not_pending' || errCode === 'invite_user_not_found') {
          setError({ type: 'invitation_invalid', message: 'Convite inválido ou indisponível. Solicite um novo convite.' });
          return;
        }
        if (errCode === 'weak_password') {
          setError({ type: 'validation', message: humanizeServerError(errCode, data?.message) });
          return;
        }
        setError({ type: 'update_failed', message: humanizeServerError(errCode, data?.message) });
        return;
      }

      const emailToLogin = String(data?.email || invitation?.email || '').trim().toLowerCase();
      if (emailToLogin) {
        try {
          await loginWithPassword(supabase, { email: emailToLogin, password: form.password });
        } catch {
          setError({
            type: 'warning',
            message: 'Senha definida com sucesso, mas não foi possível entrar automaticamente. Faça login para continuar.',
          });
          setSuccess(true);
          clearRouteState();
          return;
        }
      } else {
        setError({
          type: 'warning',
          message: 'Senha definida com sucesso, mas não foi possível entrar automaticamente. Faça login para continuar.',
        });
        setSuccess(true);
        clearRouteState();
        return;
      }

      setSuccess(true);
      clearRouteState();
      window.location.replace('/');
      return;
    } catch (err) {
      setError({
        type: 'network_error',
        message: 'Erro de conexão. Tente novamente.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-slate-950">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
            <p className="text-slate-300">Validando convite...</p>
          </div>
        </div>
        <PublicWhatsAppSupportButton
          whatsappNumber={whatsappSupport?.number}
          enabled={Boolean(whatsappSupport?.enabled)}
          message="Olá! Preciso de suporte com o aceite de convite na plataforma SOFERA."
        />
      </>
    );
  }

  if (error && (error.type === 'invitation_expired' || error.type === 'invitation_invalid')) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
          <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8 text-center">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-xl font-black text-white mb-2">Convite ou Sessão Inválida</h1>
            <p className="text-slate-400 mb-6">{error.message}</p>
            <button
              onClick={() => { clearRouteState(); window.location.href = '/'; }}
              className="w-full h-11 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition"
            >
              Voltar ao Login
            </button>
          </div>
        </div>
        <PublicWhatsAppSupportButton
          whatsappNumber={whatsappSupport?.number}
          enabled={Boolean(whatsappSupport?.enabled)}
          message="Olá! Preciso de suporte porque meu convite da plataforma SOFERA está inválido ou expirado."
        />
      </>
    );
  }

  if (success) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
          <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-black text-white mb-2">Senha Definida!</h1>
            <p className="text-slate-400 mb-6">
              {error?.type === 'warning'
                ? error.message
                : 'Sua senha foi configurada com sucesso. Você já pode acessar a plataforma.'}
            </p>
            <button
              onClick={() => { clearRouteState(); window.location.href = '/'; }}
              className="w-full h-11 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition"
            >
              Ir para a Plataforma
            </button>
          </div>
        </div>
        <PublicWhatsAppSupportButton
          whatsappNumber={whatsappSupport?.number}
          enabled={Boolean(whatsappSupport?.enabled)}
          message="Olá! Preciso de suporte relacionado ao meu acesso na plataforma SOFERA."
        />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-12">
        <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-black text-white mb-2">Aceitar Convite</h1>
            <p className="text-slate-400">
              {invitation?.email && (
                <span className="flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  {invitation.email}
                </span>
              )}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Nova Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-slate-800 text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-orange-500 pr-12"
                  placeholder="Digite sua nova senha"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-400">Força da senha</span>
                  <span className={`font-medium ${passwordStrength.score >= 3 ? 'text-green-400' : 'text-orange-400'}`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${Math.max((passwordStrength.score / 5) * 100, form.password ? 6 : 0)}%` }}
                  />
                </div>
                <p className="mt-2 text-[11px] text-slate-400">
                  Atenda a todos os requisitos abaixo.
                </p>
              </div>

              <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/60 p-3 space-y-2">
                {passwordChecks.map((check) => (
                  <div key={check.key} className="flex items-center gap-2 text-xs">
                    {check.met ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    )}
                    <span className={check.met ? 'text-green-300' : 'text-slate-400'}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Confirmar Senha
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-xl bg-slate-950/70 border text-slate-100 placeholder:text-slate-600 outline-none transition pr-12 ${form.confirmPassword ? (passwordsMatch ? 'border-green-500/60 focus:border-green-500' : 'border-red-500/60 focus:border-red-500') : 'border-slate-800 focus:border-orange-500'}`}
                  placeholder="Confirme sua senha"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={form.acceptTerms}
                  onChange={(e) => setForm(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                  className="mt-1 w-4 h-4 rounded border-slate-700 bg-slate-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                />
                <label htmlFor="acceptTerms" className="text-sm text-slate-300 leading-relaxed">
                  Li e aceito os{' '}
                  <button type="button" onClick={() => (onOpenTermsOfUse ? onOpenTermsOfUse() : window.open('/terms', '_blank', 'noopener'))} className="text-orange-400 hover:text-orange-300 underline">
                    termos de uso
                  </button>{' '}
                  e{' '}
                  <button type="button" onClick={() => (onOpenPrivacyPolicy ? onOpenPrivacyPolicy() : window.open('/privacy', '_blank', 'noopener'))} className="text-orange-400 hover:text-orange-300 underline">
                    política de privacidade
                  </button>
                </label>
              </div>
            </div>

            {error && error.type !== 'invitation_invalid' && error.type !== 'invitation_expired' && (
              <div className={`rounded-lg border p-3 ${error.type === 'warning' ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle className={`w-4 h-4 flex-shrink-0 ${error.type === 'warning' ? 'text-yellow-400' : 'text-red-400'}`} />
                  <p className={`text-sm ${error.type === 'warning' ? 'text-yellow-200' : 'text-red-200'}`}>{error.message}</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !form.password || !form.confirmPassword || !form.acceptTerms || !passwordRulesPassed || !passwordsMatch}
              className="w-full h-12 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Aceitar Convite
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">
              Já tem uma conta?{' '}
              <a href="/" onClick={(e) => { try { e.preventDefault(); } catch {}; clearRouteState(); window.location.href = '/'; }} className="text-orange-400 hover:text-orange-300 underline">
                Fazer login
              </a>
            </p>
          </div>
        </div>
      </div>
      <PublicWhatsAppSupportButton
        whatsappNumber={whatsappSupport?.number}
        enabled={Boolean(whatsappSupport?.enabled)}
        message={invitation?.email
          ? `Olá! Preciso de suporte com o aceite do convite da plataforma SOFERA. E-mail do convite: ${invitation.email}`
          : 'Olá! Preciso de suporte com o aceite de convite na plataforma SOFERA.'}
      />
    </>
  );
}
